import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface BadgeOption {
  label: string;
  value: any;
}

@Component({
  selector: 'app-badge-reusable',
  standalone: true,
  imports: [CommonModule],
  template: `
    <span
      #badgeElement
      [ngClass]="computedClasses"
      (click)="onBadgeClick($event)"
    >
      @if (loading) {
        <svg class="h-3 w-3 animate-spin text-current shrink-0" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      } @else if (dot) {
        <span
          class="w-1.5 h-1.5 shrink-0 rounded-full"
          [ngClass]="dotColor"
        ></span>
      }
      <span [class.truncate]="truncate">{{ text }}</span>
      @if (removable) {
        <button
          (click)="onRemove($event)"
          class="text-xs opacity-70 hover:opacity-100 ml-1"
          >
          ×
        </button>
      }
      @if (editable) {
        <svg
          class="h-3.5 w-3.5 shrink-0 transition-transform duration-150"
          [class.rotate-180]="isOpen"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      }
    </span>

    @if (editable && isOpen && menuPosition) {
      <div
        class="badge-reusable-menu fixed z-[1300] min-w-[145px] rounded-xl border border-slate200 bg-white p-1.5 shadow-xl"
        [style.top.px]="menuPosition.top"
        [style.left.px]="menuPosition.left"
        (click)="$event.stopPropagation()"
      >
        @for (option of normalizedOptions; track getOptionValue(option)) {
          <button
            type="button"
            class="block w-full rounded-lg px-3.5 py-2 text-left text-xs font-medium text-slate800 transition hover:bg-slate50 cursor-pointer"
            [class.bg-slate-50]="isSelected(option)"
            (click)="selectOption($event, option)"
          >
            {{ getOptionLabel(option) }}
          </button>
        }
      </div>
    }
  `,
})
export class BadgeComponent implements OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('badgeElement') private badgeElement?: ElementRef<HTMLElement>;

  @Input() text: string = 'Badge';

  @Input() variant:
    | 'neutral'
    | 'red'
    | 'amber'
    | 'green'
    | 'blue'
    | 'blue800'
    | 'indigo'
    | 'purple'
    | 'orange'
    | 'emerald'
    | 'teal'
    | 'cyan'
    | 'sky'
    | 'violet'
    | 'fuchsia'
    | 'pink'
    | 'rose'
    | 'lime'
    | 'yellow'
    | 'stone' = 'neutral';

  @Input() size: 'xs'|'sm' | 'md' = 'md';
  @Input() bordered: boolean = true;
  @Input() flat: boolean = false;
  @Input() dot: boolean = false;
  @Input() removable: boolean = false;
  @Input() truncate: boolean = false;

  /** Edit state properties */
  @Input() editable: boolean = false;
  @Input() options: Array<string | BadgeOption> = [];
  @Input() value?: any;
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;

  @Output() remove = new EventEmitter<void>();
  @Output() optionSelect = new EventEmitter<any>();
  @Output() valueChange = new EventEmitter<any>();
  @Output() selectedOption = new EventEmitter<BadgeOption>();

  isOpen = false;
  menuPosition: { top: number; left: number; placement: 'above' | 'below' } | null = null;
  private scrollListener?: () => void;

  onRemove(event?: MouseEvent) {
    event?.stopPropagation();
    this.remove.emit();
  }

  onBadgeClick(event: MouseEvent): void {
    if (!this.editable || this.disabled || this.loading) {
      return;
    }

    event.stopPropagation();
    if (this.isOpen) {
      this.closeMenu();
      return;
    }

    this.isOpen = true;
    this.updateMenuPosition();
    this.startScrollTracking();
    this.cdr.markForCheck();
  }

  selectOption(event: MouseEvent, option: BadgeOption): void {
    event.stopPropagation();
    this.closeMenu();
    this.optionSelect.emit(option.value);
    this.valueChange.emit(option.value);
    this.selectedOption.emit(option);
  }

  closeMenu(): void {
    this.isOpen = false;
    this.menuPosition = null;
    this.stopScrollTracking();
    this.cdr.markForCheck();
  }

  get normalizedOptions(): BadgeOption[] {
    return (this.options ?? []).map((opt) => {
      if (typeof opt === 'string') {
        return { label: opt, value: opt };
      }
      return opt;
    });
  }

  getOptionLabel(option: BadgeOption): string {
    return option.label;
  }

  getOptionValue(option: BadgeOption): any {
    return option.value;
  }

  isSelected(option: BadgeOption): boolean {
    const current = this.value !== undefined ? this.value : this.text;
    if (current === undefined || current === null) {
      return false;
    }
    const optVal = String(option.value).toLowerCase().replace(/\s+/g, '');
    const currentVal = String(current).toLowerCase().replace(/\s+/g, '');
    const optLabel = String(option.label).toLowerCase().replace(/\s+/g, '');
    return optVal === currentVal || optLabel === currentVal;
  }

  private updateMenuPosition(): void {
    const trigger = this.badgeElement?.nativeElement ?? this.elementRef.nativeElement;
    const rect = trigger.getBoundingClientRect();
    const menuHeight = Math.min(280, this.normalizedOptions.length * 36 + 12);
    const menuWidth = Math.max(145, rect.width);
    const gap = 4;

    const spaceBelow = window.innerHeight - rect.bottom;
    const placement: 'above' | 'below' =
      spaceBelow < menuHeight && rect.top > menuHeight ? 'above' : 'below';

    const top = placement === 'above' ? rect.top - menuHeight - gap : rect.bottom + gap;
    let left = rect.left;
    if (left + menuWidth > window.innerWidth - 12) {
      left = window.innerWidth - menuWidth - 12;
    }
    if (left < 12) {
      left = 12;
    }

    this.menuPosition = { top, left, placement };
    this.cdr.markForCheck();
  }

  private startScrollTracking(): void {
    this.stopScrollTracking();
    const onScroll = () => {
      if (this.isOpen) {
        const trigger = this.badgeElement?.nativeElement ?? this.elementRef.nativeElement;
        const rect = trigger.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) {
          this.closeMenu();
        } else {
          this.updateMenuPosition();
        }
      }
    };
    const options: AddEventListenerOptions = { capture: true, passive: true };
    document.addEventListener('scroll', onScroll, options);
    this.scrollListener = () => {
      document.removeEventListener('scroll', onScroll, options);
    };
  }

  private stopScrollTracking(): void {
    if (this.scrollListener) {
      this.scrollListener();
      this.scrollListener = undefined;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }
    const target = event.target as HTMLElement | null;
    const trigger = this.badgeElement?.nativeElement ?? this.elementRef.nativeElement;
    if (trigger.contains(target) || target?.closest('.badge-reusable-menu')) {
      return;
    }
    this.closeMenu();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeMenu();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isOpen) {
      this.updateMenuPosition();
    }
  }

  ngOnDestroy(): void {
    this.stopScrollTracking();
  }

  private base =
    'inline-flex max-w-full items-center justify-center box-border rounded-md BH6 leading-4';

  private sizeMap = {
    xs: 'px-1.5 py-0.5 gap-1 text-[11px] leading-4',
    sm: 'px-[10px] py-[5px] gap-[5px] text-[12px]',
    md: ' px-[10px] py-[5px] gap-[5px] text-[12px]',
  };

  private readonly variantMap: Record<
    NonNullable<BadgeComponent['variant']>,
    { bg: string; border: string; text: string; dot: string }
  > = {
    neutral: {
      bg: 'bg-slate50',
      border: 'border-slate200',
      text: 'text-slate800',
      dot: 'bg-slate600',
    },
    red: {
      bg: 'bg-red50',
      border: 'border-red200',
      text: 'text-red800',
      dot: 'bg-red600',
    },
    amber: {
      bg: 'bg-amber50',
      border: 'border-amber200',
      text: 'text-amber800',
      dot: 'bg-amber600',
    },
    green: {
      bg: 'bg-green50',
      border: 'border-green200',
      text: 'text-green800',
      dot: 'bg-green600',
    },
    blue: {
      bg: 'bg-blue50',
      border: 'border-blue200',
      text: 'text-blue800',
      dot: 'bg-blue600',
    },
    blue800: {
      bg: 'bg-blue50',
      border: 'border-blue200',
      text: 'text-blue800',
      dot: 'bg-blue600',
    },
    indigo: {
      bg: 'bg-indigo50',
      border: 'border-indigo200',
      text: 'text-indigo800',
      dot: 'bg-indigo600',
    },
    purple: {
      bg: 'bg-purple50',
      border: 'border-purple200',
      text: 'text-purple800',
      dot: 'bg-purple600',
    },
    orange: {
      bg: 'bg-[#FFF7ED]',
      border: 'border-[#FED7AA]',
      text: 'text-[#9A3412]',
      dot: 'bg-[#f0af55]',
    },
    // Extra colours, so a long list of statuses can give each one its own badge.
    emerald: { bg: 'bg-[#ECFDF5]', border: 'border-[#A7F3D0]', text: 'text-[#065F46]', dot: 'bg-[#059669]' },
    teal: { bg: 'bg-[#F0FDFA]', border: 'border-[#99F6E4]', text: 'text-[#115E59]', dot: 'bg-[#0D9488]' },
    cyan: { bg: 'bg-[#ECFEFF]', border: 'border-[#A5F3FC]', text: 'text-[#155E75]', dot: 'bg-[#0891B2]' },
    sky: { bg: 'bg-[#F0F9FF]', border: 'border-[#BAE6FD]', text: 'text-[#075985]', dot: 'bg-[#0284C7]' },
    violet: { bg: 'bg-[#F5F3FF]', border: 'border-[#DDD6FE]', text: 'text-[#5B21B6]', dot: 'bg-[#7C3AED]' },
    fuchsia: { bg: 'bg-[#FDF4FF]', border: 'border-[#F5D0FE]', text: 'text-[#86198F]', dot: 'bg-[#C026D3]' },
    pink: { bg: 'bg-[#FDF2F8]', border: 'border-[#FBCFE8]', text: 'text-[#9D174D]', dot: 'bg-[#DB2777]' },
    rose: { bg: 'bg-[#FFF1F2]', border: 'border-[#FECDD3]', text: 'text-[#9F1239]', dot: 'bg-[#E11D48]' },
    lime: { bg: 'bg-[#F7FEE7]', border: 'border-[#D9F99D]', text: 'text-[#3F6212]', dot: 'bg-[#65A30D]' },
    yellow: { bg: 'bg-[#FEFCE8]', border: 'border-[#FEF08A]', text: 'text-[#854D0E]', dot: 'bg-[#CA8A04]' },
    stone: { bg: 'bg-[#FAFAF9]', border: 'border-[#E7E5E4]', text: 'text-[#44403C]', dot: 'bg-[#78716C]' },
  };

  private readonly flatDotMap: Record<NonNullable<BadgeComponent['variant']>, string> = {
    neutral: 'bg-slate400',
    red: 'bg-red400',
    amber: 'bg-amber400',
    green: 'bg-green400',
    blue: 'bg-blue400',
    blue800: 'bg-blue800',
    indigo: 'bg-indigo400',
    purple: 'bg-purple400',
    orange: 'bg-[#f0af55]',
    emerald: 'bg-[#34D399]',
    teal: 'bg-[#2DD4BF]',
    cyan: 'bg-[#22D3EE]',
    sky: 'bg-[#38BDF8]',
    violet: 'bg-[#A78BFA]',
    fuchsia: 'bg-[#E879F9]',
    pink: 'bg-[#F472B6]',
    rose: 'bg-[#FB7185]',
    lime: 'bg-[#A3E635]',
    yellow: 'bg-[#FACC15]',
    stone: 'bg-[#A8A29E]',
  };

  get computedClasses(): string {
    const v = this.variantMap[this.variant];
    let bgClass = '';
    let borderClass = '';
    let textClass = v.text;

    if (this.flat) {
      bgClass = v.bg;
      borderClass = 'border border-transparent';
    }
    else if (this.bordered && this.dot && !this.removable) {
      bgClass = 'bg-white';
      borderClass = `border ${v.border}`;
      textClass = 'text-slate800';
    }
    else if (this.bordered && this.removable) {
      bgClass = v.bg;
      borderClass = `border ${v.border}`;
    }
    else if (this.bordered) {
      bgClass = v.bg;
      borderClass = `border ${v.border}`;
    }
    else {
      bgClass = v.bg;
      borderClass = 'border border-transparent';
    }

    const classes = [
      this.base,
      this.sizeMap[this.size],
      
      bgClass,
      borderClass,
      textClass,
    ];

    if (this.editable) {
      if (this.disabled) {
        classes.push('cursor-not-allowed opacity-60');
      } else {
        classes.push('cursor-pointer hover:opacity-90 transition-opacity select-none');
      }
    }

    return classes.join(' ');
  }
  get dotColor(): string {
    return this.flat ? this.flatDotMap[this.variant] : this.variantMap[this.variant].dot;
  }
}
